import { Transfer, Item, Sale } from '@/types';

export function calcTransfer(
  amount: number,
  type: 'client_paid_me' | 'i_paid_client',
  myRate: number,
  providerRate: number
): Omit<Transfer, 'id' | 'date' | 'clientId' | 'clientName' | 'transactionId' | 'type' | 'myCommissionRate' | 'providerCommissionRate' | 'status' | 'isSettled' | 'notes' | 'amount'> {
  const myCommission = (amount * myRate) / 100;
  const providerCommission = (amount * providerRate) / 100;
  const myProfit = myCommission - providerCommission;
  const totalWithMyCommission = amount + myCommission;
  const totalWithProviderCommission = amount + providerCommission;
  const netAmountDue = type === 'client_paid_me'
    ? amount + myCommission
    : amount - myCommission;

  return {
    myCommission: parseFloat(myCommission.toFixed(2)),
    providerCommission: parseFloat(providerCommission.toFixed(2)),
    myProfit: parseFloat(myProfit.toFixed(2)),
    totalWithMyCommission: parseFloat(totalWithMyCommission.toFixed(2)),
    totalWithProviderCommission: parseFloat(totalWithProviderCommission.toFixed(2)),
    netAmountDue: parseFloat(netAmountDue.toFixed(2)),
  };
}

export function calcItemTotals(purchasePrice: number, quantity: number): number {
  return parseFloat((purchasePrice * quantity).toFixed(2));
}

export function calcSale(
  sellingPrice: number,
  quantity: number,
  discount: number,
  purchasePrice: number
): { cogs: number; grossProfit: number; netProfit: number; totalSaleValue: number } {
  const totalSaleValue = parseFloat((sellingPrice * quantity - discount).toFixed(2));
  const cogs = parseFloat((purchasePrice * quantity).toFixed(2));
  const grossProfit = parseFloat((sellingPrice * quantity - cogs).toFixed(2));
  const netProfit = parseFloat((totalSaleValue - cogs).toFixed(2));
  return { cogs, grossProfit, netProfit, totalSaleValue };
}

export function formatCurrency(amount: number, currency = 'CAD'): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function exportToCSV(data: Record<string, unknown>[], filename: string): void {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    headers.map(h => {
      const val = row[h];
      const str = val === null || val === undefined ? '' : String(val);
      return str.includes(',') ? `"${str}"` : str;
    }).join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function printHtmlDocument(html: string, _title: string): void {
  // Encode HTML as a data URL to ensure proper load event firing
  const blob = new Blob([html], { type: 'text/html' });
  const blobUrl = URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:800px;height:600px;border:none;visibility:hidden;';
  document.body.appendChild(iframe);

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (_) {
      // fallback: open in new tab
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 500);
      }
    }
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(blobUrl);
    }, 3000);
  };

  iframe.src = blobUrl;
}

export function exportToPDF(title: string, headers: string[], rows: string[][]): void {
  const tableRows = rows.map(r =>
    `<tr>${r.map(cell => `<td style="border:1px solid #ddd;padding:8px;font-size:12px">${cell}</td>`).join('')}</tr>`
  ).join('');
  const html = `<!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { font-size: 20px; margin-bottom: 16px; }
        table { border-collapse: collapse; width: 100%; }
        th { background: #f0f0f0; border: 1px solid #ddd; padding: 8px; font-size: 12px; text-align: left; }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <table>
        <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
        <tbody>${tableRows}</tbody>
      </table>
    </body>
    </html>`;
  printHtmlDocument(html, title);
}
